import { useState } from "react";
import { Button, Input, Label } from "@qrody/ui";
import { useTranslations } from "@/i18n/IntlProvider";
import type { QrCodeResponse } from "@qrody/api-client/codes";
import { formatDateOnly } from "./code-utils";

/**
 * Expiry date / clear form for DYNAMIC codes. Picker is date-only;
 * backend stores `expiresAt` as midnight UTC of the chosen day — see
 * web/.../code-detail-panel.tsx for the legacy contract.
 */
export function CodeExpiryForm({
  code,
  busy,
  onSet,
  onClear,
}: {
  code: QrCodeResponse;
  busy: boolean;
  onSet: (iso: string) => void;
  onClear: () => void;
}) {
  const t = useTranslations("app.codes.detail.expiry");
  const [local, setLocal] = useState("");
  const current = code.expiresAt;

  return (
    <div className="space-y-2">
      {current ? (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span>{`${t("current").replace("{date}", formatDateOnly(current))}`}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              if (!confirm(t("clearConfirm"))) return;
              onClear();
            }}
          >
            {busy ? t("busy") : t("clear")}
          </Button>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">{t("none")}</span>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!local) return;
          // Picker writes a calendar day; the backend interprets the
          // expiry as midnight UTC of that day (last second is exclusive),
          // matching the legacy semantics from code-detail-panel.tsx.
          const iso = `${local}T00:00:00.000Z`;
          onSet(iso);
          setLocal("");
        }}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Label htmlFor="qr-expiry" className="text-xs">
            {t("pickerLabel")}
          </Label>
          <Input
            id="qr-expiry"
            type="date"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            className="h-9 w-full"
          />
        </div>
        <Button size="sm" type="submit" disabled={busy || !local}>
          {busy ? t("busy") : t("set")}
        </Button>
      </form>
    </div>
  );
}
